import React, { useState } from 'react';
import { SearchIcon, FilterIcon, LayoutGridIcon, LayoutListIcon, PaletteIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { useDarkMode } from '../../context/DarkModeContext';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { mapCreativeToProfileCard } from '../../utils/profileCardMappers';
// Define creative professional types
type CreativeCategory = 'all' | 'cover-design' | 'interior-art' | 'character-art' | 'merch' | 'photography' | 'models';
interface CreativeProfessional {
  id: string;
  name: string;
  title: string;
  category: CreativeCategory;
  specialties: string[];
  rating: number;
  reviewCount: number;
  image: string;
  available: boolean;
}
const AllCreativeProfiles = () => {
  const {
    darkMode
  } = useDarkMode();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CreativeCategory>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('name-asc');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Sample data for creative professionals
  const creatives: CreativeProfessional[] = [{
    id: 'cd1',
    name: 'Alex Morgan',
    title: 'Cover Designer',
    category: 'cover-design',
    specialties: ['Fantasy', 'YA', 'Illustrated Covers'],
    rating: 4.9,
    reviewCount: 127,
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'cd2',
    name: 'Jordan Lee',
    title: 'Book Cover Artist',
    category: 'cover-design',
    specialties: ['Romance', 'Contemporary', 'Typography'],
    rating: 4.7,
    reviewCount: 93,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'ia1',
    name: 'Riley Zhang',
    title: 'Interior Book Artist',
    category: 'interior-art',
    specialties: ['Chapter Headers', 'Decorative Elements', 'Maps'],
    rating: 4.8,
    reviewCount: 84,
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: false
  }, {
    id: 'ia2',
    name: 'Taylor Kim',
    title: 'Interior Layout Designer',
    category: 'interior-art',
    specialties: ['Typography', 'Page Design', 'Endpapers'],
    rating: 4.6,
    reviewCount: 56,
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'ca1',
    name: 'Morgan Rivera',
    title: 'Character Artist',
    category: 'character-art',
    specialties: ['Fantasy Characters', 'Portraits', 'Digital Art'],
    rating: 5.0,
    reviewCount: 142,
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'ca2',
    name: 'Casey Wilson',
    title: 'Character Illustrator',
    category: 'character-art',
    specialties: ['Sci-Fi', 'Comic Style', 'Concept Art'],
    rating: 4.8,
    reviewCount: 78,
    image: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'md1',
    name: 'Jamie Patel',
    title: 'Merchandise Designer',
    category: 'merch',
    specialties: ['Apparel', 'Accessories', 'Branding'],
    rating: 4.7,
    reviewCount: 63,
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'md2',
    name: 'Quinn Smith',
    title: 'Book Merchandise Specialist',
    category: 'merch',
    specialties: ['Bookmarks', 'Candles', 'Gift Boxes'],
    rating: 4.5,
    reviewCount: 47,
    image: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: false
  }, {
    id: 'pg1',
    name: 'Avery Johnson',
    title: 'Author Photographer',
    category: 'photography',
    specialties: ['Portraits', 'Book Events', 'Marketing Photos'],
    rating: 4.9,
    reviewCount: 115,
    image: 'https://images.unsplash.com/photo-1504257432389-52343af06ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'pg2',
    name: 'Skyler Thomas',
    title: 'Book Cover Photographer',
    category: 'photography',
    specialties: ['Product Photography', 'Landscapes', 'Conceptual'],
    rating: 4.6,
    reviewCount: 82,
    image: 'https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'md1',
    name: 'Jordan Ellis',
    title: 'Book Cover Model',
    category: 'models',
    specialties: ['Romance', 'Historical', 'Contemporary'],
    rating: 4.8,
    reviewCount: 74,
    image: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: true
  }, {
    id: 'md2',
    name: 'Harper Davis',
    title: 'Character Model',
    category: 'models',
    specialties: ['Fantasy', 'Period Costumes', 'Action'],
    rating: 4.7,
    reviewCount: 59,
    image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
    available: false
  }];
  // Filter creatives based on search query and selected category
  const filteredCreatives = creatives.filter(creative => {
    const matchesSearch = searchQuery === '' || creative.name.toLowerCase().includes(searchQuery.toLowerCase()) || creative.title.toLowerCase().includes(searchQuery.toLowerCase()) || creative.specialties.some(specialty => specialty.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || creative.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  // Sort logic
  filteredCreatives.sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'rating-desc':
        return b.rating - a.rating;
      case 'reviews-desc':
        return b.reviewCount - a.reviewCount;
      default:
        return 0;
    }
  });
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Creatives'
    }, {
      value: 'cover-design',
      label: 'Cover Design'
    }, {
      value: 'interior-art',
      label: 'Interior Art'
    }, {
      value: 'character-art',
      label: 'Character Art'
    }, {
      value: 'merch',
      label: 'Merch Design'
    }, {
      value: 'photography',
      label: 'Photography'
    }, {
      value: 'models',
      label: 'Models'
    }],
    value: selectedCategory,
    onChange: val => setSelectedCategory(val as CreativeCategory)
  }];
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`} data-id="element-9216" data-mp="28hbn2f2kvxrq">
      {/* Header Section */}
      <div className={`pt-24 md:pt-8 pb-6 px-4 sm:px-6 lg:px-8`} data-id="element-9217" data-mp="2m8gqztczbc7v">
        <div className="max-w-7xl mx-auto" data-id="element-9218" data-mp="2i7n5f6n0e9i5">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`} data-id="element-9219" data-mp="2i6ljg74gg2ln">
            Creative Arts Directory
          </h1>
          <p className={`text-lg max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9220" data-mp="2r7tfsjneutd3">
            Connect with talented creative professionals to bring your book to
            life. Find cover designers, interior artists, character
            illustrators, merchandise creators, photographers, and models.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-9221" data-mp="3dgqo57gmlemp">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-9222" data-mp="2ys3yocxab6hr">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-9223" data-mp="2yr2osbs08q1m">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-9224" data-mp="2urttv3l8am4h" />
            <span data-id="element-9225" data-mp="2ft895ewdfga9">Filters & Sort</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-9226" data-mp="2fvck2gsotfk1">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9227" data-mp="2ku5nthi69s70">
            <FilterSidebar title="Filters" sections={filterSections} onReset={() => {
            setSelectedCategory('all');
            setSortBy('name-asc');
            setSearchQuery('');
          }} searchable={false} data-id="element-9228" data-mp="1e0gzqoyo3kjg" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-9229" data-mp="1wr8g4ah2ob7c">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-9230" data-mp="1wpdx51pd2t5w">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-9231" data-mp="1mssqkbxp19zy">
                <div className="relative flex-1" data-id="element-9232" data-mp="1rqu7aq7ncx89">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9233" data-mp="bdvoa0ci0oan" />

                  <input type="text" placeholder="Search creatives by name, title, or specialty..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors ${darkMode ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9234" data-mp="gam20h0tz7sw" />
                </div>
                <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-9235" data-mp="26x00x6zqh63u">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? darkMode ? 'bg-gray-600 text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="Grid view" data-id="element-9236" data-mp="2fw3kec2tbniv">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-9237" data-mp="3s80cbw0sgcxi" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? darkMode ? 'bg-gray-600 text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="List view" data-id="element-9238" data-mp="u4elok5i8t7">
                    <LayoutListIcon className="h-5 w-5" data-id="element-9239" data-mp="3e87qae07ikvv" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="mb-6" data-id="element-9240" data-mp="tiieyaypph7x">
              <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-9241" data-mp="thgph1gy8s6o">
                {filteredCreatives.length} creative professional
                {filteredCreatives.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            {/* Results Section */}
            {filteredCreatives.length === 0 ? <div className={`text-center py-16 rounded-xl border-2 border-dashed ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-9242" data-mp="299as313bughs">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-9243" data-mp="1zc66p3hb2p21">
                  <PaletteIcon className={`h-8 w-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9244" data-mp="1qaymfpbmjoqy" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-9245" data-mp="1ldq0ddncv0kf">
                  No creative professionals found
                </h3>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9246" data-mp="3fntmpvqu1u9l">
                  Try adjusting your search or filter criteria to find more
                  results.
                </p>
              </div> : <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-id="element-9247" data-mp="23dsr6pa496xr">
                {filteredCreatives.map(creative => <ProfileCard key={creative.id} data={mapCreativeToProfileCard(creative)} viewMode={viewMode} data-id="element-9248" data-mp="1yg11m1wy1wwq" />)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default AllCreativeProfiles;